import { useState } from 'react';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { TaskStatusColumn } from './TaskStatusColumn';
import { TaskFormDialog } from './TaskFormDialog';
import { useTasks, Task } from '@/hooks/useTasks';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { Calendar, Mail, User, FolderOpen } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const STATUSES = [
  { id: 'not_started', label: 'Not Started', color: 'bg-gray-100' },
  { id: 'in_progress', label: 'In Progress', color: 'bg-blue-100' },
  { id: 'stuck', label: 'Stuck', color: 'bg-red-100' },
  { id: 'done', label: 'Done', color: 'bg-green-100' },
];

interface TaskBoardProps {
  view: 'board' | 'list';
  tasks: Task[];
}

export function TaskBoard({ view, tasks }: TaskBoardProps) {
  const { updateTask } = useTasks();
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('not_started');
  const isMobile = useIsMobile();

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const taskId = active.id as string;
    const newStatus = over.id as string;
    
    updateTask.mutate({ taskId, status: newStatus as any });
  };

  // Mobile: Card layout instead of table
  if (isMobile && view === 'list') {
    return (
      <>
        <div className="space-y-3">
          {tasks.map(task => (
            <div
              key={task.id}
              className="bg-card rounded-lg border p-4 cursor-pointer hover:bg-muted/50"
              onClick={() => setEditingTask(task)}
            >
              {/* Title and AI Badge */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-medium text-sm flex-1">{task.title}</h3>
                {task.created_by_ai && (
                  <Badge variant="secondary" className="text-xs shrink-0">✨ AI</Badge>
                )}
              </div>

              {/* Status and Priority */}
              <div className="flex gap-2 mb-2">
                <Badge variant="outline" className="text-xs">
                  {task.status.replace('_', ' ')}
                </Badge>
                <Badge 
                  variant={task.priority === 'critical' ? 'destructive' : 'outline'}
                  className="text-xs"
                >
                  {task.priority}
                </Badge>
              </div>

              {/* Metadata Grid */}
              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                {task.due_date && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {format(new Date(task.due_date), 'MMM d')}
                  </div>
                )}
                {task.assigned_to_name && (
                  <div className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    <span className="truncate">{task.assigned_to_name}</span>
                  </div>
                )}
                {task.project && (
                  <div className="flex items-center gap-1 col-span-2">
                    <FolderOpen className="h-3 w-3" />
                    <span className="truncate">{task.project.name}</span>
                  </div>
                )}
                {task.linked_emails_count > 0 && (
                  <div className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    {task.linked_emails_count}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <TaskFormDialog
          open={!!editingTask}
          onOpenChange={(open) => !open && setEditingTask(null)}
          task={editingTask}
        />
      </>
    );
  }

  // Desktop: Table view
  if (view === 'list') {
    return (
      <>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Task</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Emails</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasks.map(task => (
                <TableRow 
                  key={task.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setEditingTask(task)}
                >
                  <TableCell>
                    <div>
                      <div className="font-medium">{task.title}</div>
                      {task.created_by_ai && (
                        <Badge variant="secondary" className="text-xs mt-1">✨ AI</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{task.status.replace('_', ' ')}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={task.priority === 'critical' ? 'destructive' : 'outline'}>
                      {task.priority}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {task.due_date && (
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(task.due_date), 'MMM d, yyyy')}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {task.assigned_to_name && (
                      <div className="flex items-center gap-1 text-sm">
                        <User className="h-3 w-3" />
                        {task.assigned_to_name}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {task.project && (
                      <Badge variant="outline" className="text-xs">
                        <FolderOpen className="h-3 w-3 mr-1" />
                        {task.project.name}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {task.linked_emails_count > 0 && (
                      <div className="flex items-center gap-1 text-sm">
                        <Mail className="h-3 w-3" />
                        {task.linked_emails_count}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <TaskFormDialog
          open={!!editingTask}
          onOpenChange={(open) => !open && setEditingTask(null)}
          task={editingTask}
        />
      </>
    );
  }

  // Mobile: Single column with status selector
  if (isMobile) {
    return (
      <>
        {/* Status Selector for Mobile */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {STATUSES.map(status => (
            <Button
              key={status.id}
              variant={selectedStatus === status.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedStatus(status.id)}
              className="shrink-0"
            >
              {status.label}
            </Button>
          ))}
        </div>

        {/* Single Column View */}
        <DndContext onDragEnd={handleDragEnd}>
          <div className="space-y-2">
            <TaskStatusColumn
              status={STATUSES.find(s => s.id === selectedStatus)!}
              tasks={tasks.filter(t => t.status === selectedStatus)}
              onTaskClick={setEditingTask}
            />
          </div>
        </DndContext>

        <TaskFormDialog
          open={!!editingTask}
          onOpenChange={(open) => !open && setEditingTask(null)}
          task={editingTask}
        />
      </>
    );
  }

  // Tablet & Desktop: Multi-column grid
  const gridCols = typeof window !== 'undefined' && window.innerWidth < 1024 ? 'grid-cols-2' : 'grid-cols-4';

  return (
    <>
      <DndContext onDragEnd={handleDragEnd}>
        <div className={`grid gap-4 ${gridCols}`}>
          {STATUSES.map(status => (
            <TaskStatusColumn
              key={status.id}
              status={status}
              tasks={tasks.filter(t => t.status === status.id)}
              onTaskClick={setEditingTask}
            />
          ))}
        </div>
      </DndContext>

      <TaskFormDialog
        open={!!editingTask}
        onOpenChange={(open) => !open && setEditingTask(null)}
        task={editingTask}
      />
    </>
  );
}
